import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { businessId } from "../inbox/businessSetup";

type Unit = "minutes" | "hours" | "days";
type FollowChannel = "whatsapp" | "instagram" | "email" | "call";

type Answers = {
  reachAmount: string;
  reachUnit: Unit;
  followChannel: FollowChannel;
  followUps: string;
  totalTouches: string;
  dormantPercent: string;
};

const CHANNELS: Array<{ id: FollowChannel; label: string }> = [
  { id: "whatsapp", label: "WhatsApp" },
  { id: "instagram", label: "Instagram" },
  { id: "email", label: "Email" },
  { id: "call", label: "Phone call" },
];

const STORAGE_KEY = "fd24.leadRecovery.v1";
const BRAND = "#2563eb";

const EMPTY: Answers = {
  reachAmount: "",
  reachUnit: "hours",
  followChannel: "whatsapp",
  followUps: "",
  totalTouches: "",
  dormantPercent: "",
};

function loadAnswers(): Answers {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return { ...EMPTY, ...(all[businessId()] || {}) };
  } catch {
    return EMPTY;
  }
}

function saveAnswers(answers: Answers) {
  const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  all[businessId()] = answers;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

function positive(value: string): number | null {
  const amount = Number(value);
  if (!value.trim() || Number.isNaN(amount) || amount <= 0) return null;
  return amount;
}

function timesLabel(automated: number, yours: number): string {
  if (yours === automated) return "Same as FrontDesk";
  if (yours > automated) return "You do more";
  const times = Math.max(1, Math.round(automated / yours));
  return `${times}× more`;
}

export default function LeadRecoveryPage() {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState<Answers>(loadAnswers);

  const set = (patch: Partial<Answers>) => setAnswers((current) => ({ ...current, ...patch }));

  const reach = positive(answers.reachAmount);
  const reachSeconds = reach === null
    ? null
    : answers.reachUnit === "minutes"
      ? reach * 60
      : answers.reachUnit === "hours"
        ? reach * 3600
        : reach * 86400;
  const channel = CHANNELS.find((item) => item.id === answers.followChannel) || CHANNELS[0];
  const isCall = answers.followChannel === "call";
  const extraQuestion = isCall
    ? "If a customer stops replying, how many extra times do you call?"
    : `If a customer stops replying, how many extra ${channel.label} messages do you send?`;
  const totalQuestion = isCall
    ? "How many calls do you make in total before you stop trying?"
    : `How many ${channel.label} messages do you send in total before you stop trying?`;
  const followUps = positive(answers.followUps);
  const totalTouches = positive(answers.totalTouches);
  const dormant = positive(answers.dormantPercent);

  const goInbox = () => navigate("/events/conversations");

  return (
    <div className="min-h-screen bg-white px-6 py-16 text-gray-900">
      <div className="mx-auto w-full max-w-5xl">
        <h1 className="text-xl font-semibold text-gray-900">Lead recovery</h1>
        <p className="mb-6 mt-1 text-sm text-gray-500">Answer what you do today. The right column shows what FrontDesk does for the same customer.</p>

        <div className="overflow-hidden rounded-md border border-gray-200">
          <div className="grid grid-cols-[1fr_200px_150px] bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-400">
            <div />
            <div className="px-4 py-3 text-center">You</div>
            <div className="px-4 py-3 text-center">With FrontDesk</div>
          </div>

          <Row
            question="When a new customer arrives, how long do you wait before the first message?"
            helper="Count nights and Sundays too. FrontDesk sends the first message in 60 seconds."
            kraya={reachSeconds === null ? null : {
              headline: "60 sec",
              sub: `${Math.max(1, Math.round(reachSeconds / 60))}× faster`,
            }}
          >
            <input
              type="number"
              min={0}
              value={answers.reachAmount}
              onChange={(event) => set({ reachAmount: event.target.value })}
              aria-label="Hours before the first message"
              className="w-14 border-0 border-b border-gray-300 bg-transparent py-1 text-center text-sm outline-none focus:border-blue-600"
            />
            <select
              value={answers.reachUnit}
              onChange={(event) => set({ reachUnit: event.target.value as Unit })}
              aria-label="Time unit"
              className="bg-transparent text-sm text-gray-600 outline-none"
            >
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
            </select>
          </Row>

          <div className="grid grid-cols-[1fr_200px_150px] items-center border-t border-gray-100">
            <div className="px-6 py-5">
              <p className="text-sm font-medium text-gray-900">How do you follow up?</p>
              <p className="mt-1 text-xs leading-5 text-gray-400">Pick WhatsApp, Instagram, email, or a phone call. The next questions use that choice.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {CHANNELS.map((item) => {
                  const selected = answers.followChannel === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => set({ followChannel: item.id })}
                      className={`rounded-full px-3 py-1 text-sm ${
                        selected ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="px-3 text-center text-sm text-gray-700">{channel.label}</p>
            <div className="px-4 text-right">
              <p className="text-sm font-semibold" style={{ color: BRAND }}>WhatsApp and Instagram</p>
              <p className="text-xs text-gray-400">Message follow-up</p>
            </div>
          </div>

          <Row
            question={extraQuestion}
            helper="FrontDesk sends 5 more WhatsApp or Instagram messages in the first 8 hours: after 5 minutes, then 1, 2, 3, and 8 hours."
            kraya={followUps === null ? null : { headline: "5 times", sub: timesLabel(5, followUps) }}
          >
            <input
              type="number"
              min={0}
              value={answers.followUps}
              onChange={(event) => set({ followUps: event.target.value })}
              aria-label="Extra messages after they stop replying"
              className="w-20 rounded border border-gray-200 px-2 py-1 text-sm outline-none focus:border-blue-600"
            />
            <span className="text-sm text-gray-400">{isCall ? "calls" : "times"}</span>
          </Row>

          <Row
            question={totalQuestion}
            helper="FrontDesk sends up to 12 WhatsApp or Instagram messages over 2 to 3 weeks, then stops."
            kraya={totalTouches === null ? null : { headline: isCall ? "12 messages" : "12 times", sub: timesLabel(12, totalTouches) }}
          >
            <input
              type="number"
              min={0}
              value={answers.totalTouches}
              onChange={(event) => set({ totalTouches: event.target.value })}
              aria-label="Total messages before you stop"
              className="w-16 border-0 border-b border-gray-300 bg-transparent py-1 text-center text-sm outline-none focus:border-blue-600"
            />
            <span className="text-sm text-gray-400">{isCall ? "calls" : "times"}</span>
          </Row>

          <Row
            question={`Of customers quiet for 30 days or more, what percent do you reach again by ${channel.label.toLowerCase()}?`}
            helper="FrontDesk messages every one of them again on WhatsApp or Instagram. None are left waiting."
            kraya={dormant === null ? null : { headline: "100%", sub: timesLabel(100, dormant) }}
          >
            <input
              type="number"
              min={0}
              max={100}
              value={answers.dormantPercent}
              onChange={(event) => set({ dormantPercent: event.target.value })}
              aria-label="Percent of quiet leads you reach again"
              className="w-16 border-0 border-b border-gray-300 bg-transparent py-1 text-center text-sm outline-none focus:border-blue-600"
            />
            <span className="text-sm text-gray-400">%</span>
          </Row>
        </div>

        <div className="mt-8 flex items-center justify-between border-t border-gray-100 pt-6">
          <button type="button" onClick={goInbox} className="text-sm text-gray-500 hover:text-gray-800">
            Skip
          </button>
          <button
            type="button"
            onClick={() => {
              saveAnswers(answers);
              goInbox();
            }}
            className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({
  question,
  helper,
  kraya,
  children,
}: {
  question: string;
  helper: string;
  kraya: { headline: string; sub: string } | null;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[1fr_200px_150px] items-center border-t border-gray-100">
      <div className="px-6 py-5">
        <p className="text-sm font-medium text-gray-900">{question}</p>
        <p className="mt-1 text-xs leading-5 text-gray-400">{helper}</p>
      </div>
      <div className="flex items-center justify-center gap-2 px-3">{children}</div>
      <div className="px-4 text-right">
        {kraya ? (
          <>
            <p className="text-sm font-semibold" style={{ color: BRAND }}>{kraya.headline}</p>
            <p className="text-xs text-gray-400">{kraya.sub}</p>
          </>
        ) : null}
      </div>
    </div>
  );
}
